import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import {AuditLog, AuditLogType, AuditLogTypeLabels,} from "../../models/AuditLog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {MigrationFlagStatusIntLabels,} from "../../models/Flags/MigrationFlagStatus";

import { dialogNarrow } from "@/lib/dialogSize";

export const ViewFlagUpdateDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => {
  const generateDynamicLink = (entityId: string | null, type: AuditLogType) => {
    if (entityId === null) return `/organization/${organizationId}/audit-logs`;

    switch (type) {
      case AuditLogType.CaseBlockUpdate:
      case AuditLogType.CaseFlagUpdate:
        return `/organization/${organizationId}/case/${entityId}`;
      case AuditLogType.IntakeBlockUpdate:
      case AuditLogType.IntakeFlagUpdate:
        return `/organization/${organizationId}/intake/${entityId}`;
      case AuditLogType.FirmDocumentsFolderBlockUpdate:
        return `/organization/${organizationId}/firm-documents-folder/${entityId}`;
      case AuditLogType.ProviderBlockUpdate:
        return `/organization/${organizationId}/provider/${entityId}`;
      case AuditLogType.TemplatesLibraryBlockUpdate:
        return `/organization/${organizationId}/template/${entityId}`;
      default:
        return `/organization/${organizationId}/audit-logs`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${dialogNarrow} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {AuditLogTypeLabels[auditLog.eventType]}
              </DialogTitle>
            </DialogHeader>

            <Separator />

            {auditLog.additionalData && (() => {
              try {
                const additionalDataAsJson = JSON.parse(auditLog.additionalData);

                return (
                  <>
                    {additionalDataAsJson.FlagStatus !== undefined && (
                      <div className="flex flex-row gap-1">
                        <span className="text-sm font-bold">
                          Flag Status:
                        </span>
                        <span className="text-sm">
                          {MigrationFlagStatusIntLabels[additionalDataAsJson.FlagStatus]}
                        </span>
                      </div>
                    )}

                    {additionalDataAsJson.Block !== undefined && (
                      <div className="flex flex-row gap-1">
                        <span className="text-sm font-bold">
                          Block:
                        </span>
                        <span className="text-sm">
                          {additionalDataAsJson.Block ? "Blocked" : "Unblocked"}
                        </span>
                      </div>
                    )}
                  </>
                );
              } catch (error) {
                return (
                  <span className="text-sm">
                    Failed to parse additionalData
                  </span>
                );
              }
            })()}

            <div className="flex flex-row gap-1">
              <span className="text-sm font-bold">
                Migration Entity ID:
              </span>
              <span className="text-sm">
                <a
                  href={generateDynamicLink(
                    auditLog.entityId,
                    auditLog.eventType
                  )}
                  className="hover:underline text-primary"
                >
                  {auditLog.entityId}
                </a>
              </span>
            </div>

            <LabelValuePair
              label="Performed By"
              value={auditLog.performerEmailAddress || auditLog.performedBy}
            />

            <LabelValuePair
              label="Event Date"
              value={auditLog.eventDate}
            />
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};
